import type { Waiver } from '../api/use-waivers.js';
import { BusyLabel } from './busy-label.js';

/**
 * One consent: a box, its heading, and the whole text with the names in bold —
 * the camp form's layout (ruled 13 October), and the text verbatim (RE-27).
 *
 * The whole card is the label, so the tap target is the card rather than a
 * 20px square (RE-24). The text is never cut short: what is ticked is what was
 * read, and a "read more" is how a parent agrees to words they did not see.
 *
 * `saving`, where a tick saves at once (a child's page): the box is held and
 * its Required/Optional word becomes "Saving…" with the spinner (RE-35).
 */
export function ConsentBox({
  id,
  waiver,
  guardian,
  athlete,
  checked,
  onChange,
  error,
  required = false,
  saving = false,
}: {
  id: string;
  waiver: Waiver;
  guardian: string;
  athlete: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  error?: string | null;
  required?: boolean;
  saving?: boolean;
}) {
  const parts = waiver.template.split(/(\{guardian\}|\{athlete\}|\{adult\})/);

  return (
    <label className="consent-box" htmlFor={id} data-invalid={error ? true : undefined}>
      <span className="consent-head">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={saving}
          aria-busy={saving}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="consent-title">{waiver.title}</span>
        <span className="consent-need waiting">
          <BusyLabel busy={saving} working="Saving…">
            {required ? 'Required' : 'Optional'}
          </BusyLabel>
        </span>
      </span>
      <span className="consent-text">
        {parts.map((part, index) =>
          part === '{guardian}' ? (
            <b key={index}>{guardian}</b>
          ) : part === '{athlete}' || part === '{adult}' ? (
            <b key={index}>{athlete}</b>
          ) : (
            part
          ),
        )}
      </span>
      {error ? (
        <span className="consent-error" id={`${id}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
